import type { ReactNode } from 'react';

/**
 * discover-card-polish — le bloc du son : pochette, titre, artiste, sur un
 * panneau translucide.
 *
 * LE FLOU A UN SEUL RÔLE : rendre le titre et l'artiste lisibles pendant que les
 * réactions volent autour. Une jaquette est opaque, donc `backdrop-filter` ne
 * fait rien derrière elle — il n'agit que dans les gouttières et sous les deux
 * lignes de texte. Le carré est ici pour que le bloc ait une forme unifiée, pas
 * parce qu'il aurait besoin d'être flouté ; c'est pourquoi sortir le texte du
 * panneau supprimerait la seule chose que le flou accomplit.
 *
 * D'où les deux bornes ci-dessous. Sans elles le panneau prenait toute la
 * largeur de l'écran et floutait les emojis — il annulait sa raison d'être.
 */

/**
 * Largeur nominale de la pochette (`basis-44` = 11rem) et sa borne à +10 %.
 *
 * ⚠️ DÉRIVÉE, pas indépendante : si `basis-44` change, ces deux valeurs doivent
 * changer avec. La borne ne peut pas s'exprimer en pourcentage de la largeur
 * RENDUE — celle-ci est déduite de la hauteur (`aspect-square` sur un élément
 * qui rétrécit), donc s'y référer serait circulaire.
 */
const CONTENT_MAX = '12.1rem'; // 11rem × 1,1 — la pochette utilise `max-w-44`

export default function TrackPanel({
  image,
  title,
  artists,
  play,
}: {
  image: string | null;
  title: string;
  artists?: string;
  /** Le bouton lecture/pause, posé en bas à droite de la pochette. */
  play: ReactNode;
}) {
  return (
    <div className="tw:relative tw:z-10 tw:mb-4 tw:flex tw:min-h-0 tw:flex-1 tw:items-end tw:justify-center">
      <div
        data-testid="track-panel"
        // La borne porte sur le CONTENU, padding en sus : plafonner la boîte à
        // 12.1rem laisserait un texte plus étroit que la pochette — l'inverse de
        // la règle voulue.
        style={{ maxWidth: `calc(${CONTENT_MAX} + 2.5rem)` }}
        className="tw:flex tw:min-h-0 tw:max-h-full tw:flex-col tw:items-center tw:gap-2 tw:rounded-3xl tw:px-5 tw:py-3 tw:backdrop-blur-md"
      >
        {/* La pochette part de 11rem et REND de la place quand il n'y en a plus :
            `basis` donne sa hauteur, `shrink`+`min-h-0` l'autorisent à rétrécir,
            `aspect-square` recalcule la largeur pour qu'elle reste carrée. En
            taille fixe elle débordait par le haut d'une carte `justify-end
            overflow-hidden` — donc elle était rognée par le header.
            Le `max-width` est ce que `size-44` fournissait implicitement : sans
            lui, la taille naturelle de la jaquette pousse le panneau à sa borne
            en permanence au lieu de le laisser épouser un titre court. */}
        <div
          data-testid="track-panel-cover"
          className="tw:relative tw:aspect-square tw:min-h-0 tw:w-full tw:max-w-44 tw:shrink tw:basis-44 tw:overflow-hidden tw:rounded-2xl tw:bg-dark-1"
        >
          {image && <img src={image} alt="" className="tw:size-full tw:object-cover" />}
          {play}
        </div>
        <p data-testid="track-panel-title" className="tw:text-base tw:font-semibold tw:text-white">
          {title}
        </p>
        <p data-testid="track-panel-artists" className="tw:caption tw:text-grey-3">
          {artists}
        </p>
      </div>
    </div>
  );
}
